import React from 'react';
const ismatch = (ps) => (xs) => {
    if (ps.length === xs.length) {
        let result = {};
        for (let i = 0; i < xs.length; ++i) {
            if (xs[i].startsWith('$')) {
                result[xs[i].substring(1)] = ps[i];
                continue;
            }
            else if (xs[i] === ps[i]) {
                continue; // static - ignore
            }
            return null;
        }
        return result;
    }
    return null;
};
const parseHash = (x) => {
    const _raw = x;
    const raw = _raw === '' || _raw === '#' || _raw === '#/' ? '#/' : _raw;
    const pieces = ((xs) => (xs[xs.length - 1] === '' ? xs.slice(0, -1) : xs))((raw.startsWith('#/')
        ? raw.substring(2)
        : raw.startsWith('/')
            ? raw.substring(1)
            : raw).split('/'));
    return { _raw, raw, pieces, ismatch: ismatch(pieces) };
};
const normalizeBasename = (basename) => {
    if (!basename) {
        return '';
    }
    const normalized = basename.startsWith('/') ? basename : `/${basename}`;
    const trimmed = normalized.replace(/\/+$/, '');
    return trimmed === '' ? '' : trimmed;
};
const makeMatcher = (rawPieces) => ismatch(rawPieces);
const parsePath = (value, basename) => {
    const _raw = value;
    let withoutBase = value;
    if (basename && value.startsWith(basename)) {
        withoutBase = value.substring(basename.length) || '/';
    }
    const raw = withoutBase === '' ? '/' : withoutBase;
    const pieces = raw === '/' ? [] : raw.split('/').filter(Boolean);
    return { _raw, raw, pieces, ismatch: makeMatcher(pieces) };
};
const resolveCurrent = ({ mode = 'hash', basename = '' }) => {
    if (mode === 'history') {
        return parsePath(window.location.pathname, basename);
    }
    return parseHash(window.location.hash);
};
const buildHistoryTarget = (target, basename) => {
    const absolute = target.startsWith('http://') || target.startsWith('https://');
    const originMatch = target.startsWith(window.location.origin);
    if (originMatch) {
        const relative = target.substring(window.location.origin.length) || '/';
        target = relative;
    }
    else if (absolute) {
        return null;
    }
    let value = target;
    if (value.startsWith('#')) {
        value = value.substring(1);
    }
    if (!value.startsWith('/')) {
        value = `/${value}`;
    }
    if (basename && value.startsWith(basename)) {
        return value;
    }
    return `${basename}${value}`;
};
const defaultConfig = { mode: 'hash', basename: '' };
const RoutingConfigContext = React.createContext(defaultConfig);
export const RoutingProvider = ({ mode = 'hash', basename = '', children }) => {
    const value = React.useMemo(() => ({ mode, basename: normalizeBasename(basename) }), [mode, basename]);
    return React.createElement(RoutingConfigContext.Provider, { value }, children);
};
export const useRoutingConfig = () => React.useContext(RoutingConfigContext);
export const useRouting = () => {
    const config = React.useContext(RoutingConfigContext);
    const { mode, basename } = config;
    const [state, setState] = React.useState(() => resolveCurrent(config));
    React.useEffect(() => {
        setState(resolveCurrent(config));
    }, [mode, basename]);
    React.useEffect(() => {
        const eventName = mode === 'hash' ? 'hashchange' : 'popstate';
        const listener = () => setState(resolveCurrent(config));
        window.addEventListener(eventName, listener);
        return () => window.removeEventListener(eventName, listener);
    }, [mode, basename]);
    const navigate = React.useCallback((target) => {
        if (mode === 'hash') {
            const value = target.startsWith('#')
                ? target
                : target.startsWith('/')
                    ? `#${target}`
                    : `#/${target}`;
            window.location.hash = value;
            return;
        }
        const path = buildHistoryTarget(target, basename);
        if (!path) {
            window.location.href = target;
            return;
        }
        window.history.pushState(null, '', path);
        setState(resolveCurrent(config));
    }, [mode, basename, config]);
    return [state, navigate];
};
export default useRouting;
